import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ShipRatesPanel from "@/components/ShipRatesPanel";
import { HelpHint } from "@/components/HelpHint";

// Per-quote Services block (labour / onsite estimate / installation / delivery method).
// One block per quote, added to the subtotal — mirrors the Miscellaneous UX.
export function ServicesPicker({ module, toggle = "checkbox", onChange }) {
  const [enabled, setEnabled] = useState(false);
  const [zones, setZones] = useState([]);
  const [labourRate, setLabourRate] = useState(35);
  const [hidden, setHidden] = useState(false);
  const [postal, setPostal] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [labour, setLabour] = useState({ on: false, hours: 1, persons: 1 });
  const [onsite, setOnsite] = useState({ on: false });
  const [install, setInstall] = useState({ on: false, hours: 2, persons: 1 });
  const [delivery, setDelivery] = useState("none");
  const [shipRate, setShipRate] = useState(null);

  useEffect(() => {
    api.get("/service-zones").then((r) => setZones(r.data || [])).catch(() => setZones([]));
    api.get("/settings").then((r) => {
      setLabourRate(r.data?.labour_rate ?? 35);
      setHidden(r.data?.[`services_${module}_on`] === false);
    }).catch(() => {});
  }, [module]);

  useEffect(() => {
    if (!enabled) { onChange(null); return; }
    onChange({
      labour: { on: labour.on, hours: +labour.hours || 0, persons: +labour.persons || 1 },
      onsite: { on: onsite.on, zone_id: zoneId },
      install: { on: install.on, zone_id: zoneId, hours: +install.hours || 0, persons: +install.persons || 1 },
      delivery: delivery === "shipping"
        ? { method: "shipping", carrier: shipRate?.carrier || "", service_name: shipRate?.service_name || "", service_code: shipRate?.service_code || "", amount: shipRate?.total || 0 }
        : { method: delivery, zone_id: zoneId },
    });
  }, [enabled, labour, onsite, install, delivery, zoneId, shipRate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fsa = (postal || "").replace(/\s+/g, "").toUpperCase().slice(0, 3);
    if (fsa.length === 3) {
      const z = zones.find((zz) => (zz.postal_prefixes || []).includes(fsa));
      if (z) setZoneId(z.id);
    }
  }, [postal, zones]);

  if (hidden) return null;
  const zone = zones.find((z) => z.id === zoneId);

  const ZoneSelect = () => (
    <Select value={zoneId || undefined} onValueChange={setZoneId}>
      <SelectTrigger data-testid="services-zone" className="h-8 text-xs rounded-lg w-full"><SelectValue placeholder="Select area / zone" /></SelectTrigger>
      <SelectContent>{zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div className={toggle === "switch" ? "mt-4 border-t border-slate-100 pt-3" : "mt-4"} data-testid="services-picker">
      {toggle === "switch" ? (
        <div className="flex items-center justify-between">
          <Label className="text-xs flex items-center gap-1">Services<HelpHint side="right">Add labour, onsite estimate, installation and delivery/shipping to this quote. Enter the client&apos;s postal code to auto-detect the zone and rates.</HelpHint></Label>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="services-toggle" />
        </div>
      ) : (
        <span className="inline-flex items-center gap-1">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="services-toggle" />
            Services
          </label>
          <HelpHint side="right">Add labour, onsite estimate, installation and delivery/shipping to this quote. Enter the client&apos;s postal code to auto-detect the zone and rates.</HelpHint>
        </span>
      )}

      {enabled && (
        <div className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="services-body">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-mono uppercase tracking-wide text-slate-400">Client postal code (auto-detects zone)</span>
            <div className="flex gap-2">
              <Input value={postal} onChange={(e) => setPostal(e.target.value)} placeholder="e.g. V5C 1A1" data-testid="services-postal" className="h-8 text-xs rounded-lg w-32 uppercase" />
              <div className="flex-1"><ZoneSelect /></div>
            </div>
          </div>

          {/* Labour */}
          <div className="rounded-lg bg-white border border-slate-200 p-2.5 space-y-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" checked={labour.on} onChange={(e) => setLabour({ ...labour, on: e.target.checked })} data-testid="svc-labour-on" />
              Labour (shop) · ${labourRate}/hr
            </label>
            {labour.on && (
              <div className="flex items-center gap-2 pl-5">
                <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">Hours</span>
                  <Input type="number" min={0} step="0.5" value={labour.hours} onChange={(e) => setLabour({ ...labour, hours: e.target.value })} data-testid="svc-labour-hours" className="h-7 w-20 rounded num text-right" /></label>
                <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">Persons</span>
                  <Input type="number" min={1} value={labour.persons} onChange={(e) => setLabour({ ...labour, persons: e.target.value })} data-testid="svc-labour-persons" className="h-7 w-20 rounded num text-right" /></label>
              </div>
            )}
          </div>

          {/* Onsite estimate */}
          <div className="rounded-lg bg-white border border-slate-200 p-2.5">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" checked={onsite.on} onChange={(e) => setOnsite({ on: e.target.checked })} data-testid="svc-onsite-on" />
              Onsite estimate{zone ? ` · $${zone.onsite_fee}` : " (select zone)"}
            </label>
          </div>

          {/* Installation */}
          <div className="rounded-lg bg-white border border-slate-200 p-2.5 space-y-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" checked={install.on} onChange={(e) => setInstall({ ...install, on: e.target.checked })} data-testid="svc-install-on" />
              Installation{zone ? ` · $${zone.install_rate}/hr/person` : " (select zone)"}
            </label>
            {install.on && (
              <div className="flex items-center gap-2 pl-5">
                <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">Hours</span>
                  <Input type="number" min={0} step="0.5" value={install.hours} onChange={(e) => setInstall({ ...install, hours: e.target.value })} data-testid="svc-install-hours" className="h-7 w-20 rounded num text-right" /></label>
                <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">Persons</span>
                  <Input type="number" min={1} value={install.persons} onChange={(e) => setInstall({ ...install, persons: e.target.value })} data-testid="svc-install-persons" className="h-7 w-20 rounded num text-right" /></label>
              </div>
            )}
          </div>

          {/* Delivery method */}
          <div className="rounded-lg bg-white border border-slate-200 p-2.5 space-y-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wide text-slate-400">Delivery method</span>
            <div className="flex flex-col gap-1 text-xs">
              {[["none", "None"], ["shipping", "Shipping (live rates · ShipNerd)"], ["local", zone ? `Local delivery · $${zone.local_delivery_fee}` : "Local delivery (select zone)"], ["pickup", "Store pickup · Free"]].map(([val, lbl]) => (
                <label key={val} className="flex items-center gap-2 cursor-pointer text-slate-700">
                  <input type="radio" name={`delivery-${module}`} checked={delivery === val} onChange={() => { setDelivery(val); if (val !== "shipping") setShipRate(null); }} data-testid={`svc-delivery-${val}`} />
                  {lbl}
                  {val === "shipping" && shipRate && <span className="ml-auto font-semibold text-[#2495D3]">${(shipRate.total || 0).toFixed(2)}</span>}
                </label>
              ))}
            </div>
            {delivery === "shipping" && <ShipRatesPanel defaultZip={postal} selected={shipRate} onRate={setShipRate} />}
          </div>
        </div>
      )}
    </div>
  );
}
